import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDateRangePicker, MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTimepicker, MatTimepickerModule, MatTimepickerOption } from '@angular/material/timepicker';
import { TNil } from '@app/app.interface';
import { ICampaignForm } from '@campaign-form/campaign-form.interface';
import { ECampaignStatus } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { CampaignDurationUtilitiesService } from '@utilities/campaign-duration-utilities.service';
import { DatePickerUtilitiesService } from '@utilities/date-picker-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { TimepickerUtilitiesService } from '@utilities/timepicker-utilities.service';
import { addMinutes, endOfDay, isBefore, isSameDay, startOfDay } from 'date-fns';
import { isNil } from 'lodash';
import { Subscription } from 'rxjs';
import { CampaignDatesService } from './campaign-dates.service';

@Component({
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatErrorComponent,
    TranslateModule,
    MatButtonModule,
    IconComponent,
    DatePipe,
  ],
  providers: [provideNativeDateAdapter()],
  selector: 'app-campaign-dates',
  templateUrl: './campaign-dates.component.html',
})
export class CampaignDatesComponent implements OnInit, OnDestroy {
  public readonly form = input.required<FormGroup<ICampaignForm>>();

  public readonly campaignStatus = input<ECampaignStatus | TNil>(ECampaignStatus.draft);

  public readonly isViolatingMinimumDuration = signal<boolean>(false);
  public readonly campaignDatesError = signal<string | null>(null);

  public readonly isStartInThePast = computed(() => {
    const startDateTime = this.dateUtils.combineDateTime(this.startDateSignal()!, this.startTimeSignal()!);

    return (
      startDateTime &&
      isBefore(startDateTime, startOfDay(new Date())) &&
      this.campaignStatus() === ECampaignStatus.draft
    );
  });

  public allTimeOptions: MatTimepickerOption<Date>[] = [];

  public startTimeFilteredOptions: MatTimepickerOption<Date>[] = [];
  public endTimeFilteredOptions: MatTimepickerOption<Date>[] = [];

  public readonly datePickerUtilities = inject(DatePickerUtilitiesService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly campaignDatesService = inject(CampaignDatesService);
  private readonly timepickerUtilities = inject(TimepickerUtilitiesService);
  private readonly campaignDurationUtilitiesService = inject(CampaignDurationUtilitiesService);

  private readonly startDateSignal = signal<Date | null>(null);
  private readonly startTimeSignal = signal<Date | null>(null);
  private readonly endDateSignal = signal<Date | null>(null);
  private readonly endTimeSignal = signal<Date | null>(null);

  private readonly startRangePicker = viewChild.required<MatDateRangePicker<Date>>('startRangePicker');
  private readonly endDatePicker = viewChild.required<MatDatepicker<Date>>('endDatePicker');

  private readonly subscriptions: Subscription[] = [];

  private isSyncingPicker = false;
  private subscriptionsInitialized = false;
  private previousFormRef: FormGroup<ICampaignForm> | null = null;

  constructor() {
    effect(() => {
      const form = this.form();
      const formRefChanged = form !== this.previousFormRef;

      if (formRefChanged) {
        if (this.subscriptionsInitialized) {
          this.unsubscribeAll();
          this.subscribeToDateTimeChanges();
          this.subscribeToPickerEvents();
        }

        const startDate = form.get('startDate')?.value;
        const endDate = form.get('endDate')?.value;

        if (startDate === null || startDate === undefined) {
          form.get('startDate')?.setValue(startOfDay(new Date()), { emitEvent: false });
          form.get('startTime')?.setValue(startOfDay(new Date()), { emitEvent: false });
        }

        if (endDate === null || endDate === undefined) {
          form.get('endDate')?.setValue(endOfDay(new Date()), { emitEvent: false });
          form.get('endTime')?.setValue(endOfDay(new Date()), { emitEvent: false });
        }

        this.previousFormRef = form;
      }
    });
  }

  public ngOnInit(): void {
    this.subscribeToDateTimeChanges();
    this.subscribeToPickerEvents();
    this.subscriptionsInitialized = true;
    this.previousFormRef = this.form();
    this.allTimeOptions = this.timepickerUtilities.generateTimeOptions();
    this.startTimeFilteredOptions = [...this.allTimeOptions];
    this.endTimeFilteredOptions = [...this.allTimeOptions];

    this.syncPickerSelections(true);
  }

  public openStartPicker(): void {
    if (this.endDatePicker()) {
      this.endDatePicker().close();
    }

    setTimeout(() => this.startRangePicker().open());
  }

  public openEndPicker(): void {
    if (this.startRangePicker()) {
      this.startRangePicker().close();
    }

    setTimeout(() => this.endDatePicker().open());
  }

  public onRangeStartDateChange(date: Date | null): void {
    if (!date) {
      return;
    }

    this.form().get('startDate')?.setValue(date);

    const startTimeValue: Date | null = this.form()?.get('startTime')?.value as Date | null;

    if (startTimeValue) {
      this.form()?.get('startTime')?.setValue(this.dateUtils.combineDateTime(date, startTimeValue));
    }
  }

  public onRangeEndDateChange(date: Date | null): void {
    const validEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
      date,
      (this.form().get('startDate')?.value as Date | null) ?? null,
    );

    if (!validEndDate) {
      return;
    }

    this.form().get('endDate')?.setValue(validEndDate);

    const endTimeValue: Date | null = this.form()?.get('endTime')?.value as Date | null;

    if (endTimeValue) {
      this.form()?.get('endTime')?.setValue(this.dateUtils.combineDateTime(validEndDate, endTimeValue));
    }
  }

  public onInputChange(event: Event, timepicker: MatTimepicker<Date>): void {
    const inputElement = event.target as HTMLInputElement;
    const inputValue = inputElement?.value || '';

    this.startTimeFilteredOptions = this.timepickerUtilities.filterTimeOptions(inputValue, this.allTimeOptions);
    this.endTimeFilteredOptions = this.timepickerUtilities.filterTimeOptions(inputValue, this.allTimeOptions);

    timepicker.close();
    setTimeout(() => timepicker.open());
  }

  public selectAllText(event: FocusEvent): void {
    const timeInput = event.target as HTMLInputElement;
    timeInput.select();
  }

  public ngOnDestroy(): void {
    this.unsubscribeAll();
  }

  private subscribeToDateTimeChanges(): void {
    this.subscribeToStartDateTimeChanges();
    this.subscribeToEndDateTimeChanges();

    this.startDateSignal.set(this.form()?.get('startDate')?.value || null);
    this.startTimeSignal.set(this.form()?.get('startTime')?.value || null);
    this.endDateSignal.set(this.form()?.get('endDate')?.value || null);
    this.endTimeSignal.set(this.form()?.get('endTime')?.value || null);

    this.campaignDatesService.setStartDate(this.startTimeSignal()!);
    this.campaignDatesService.setEndDate(this.endTimeSignal()!);
  }

  private subscribeToStartDateTimeChanges(): void {
    const startDateControl = this.form().get('startDate');

    if (startDateControl) {
      this.subscriptions.push(
        startDateControl.valueChanges.subscribe((value) => {
          this.startDateSignal.set(value);

          if (this.form()?.get('endDate')?.value) {
            this.form()?.get('endDate')?.markAsTouched();
          }

          const startTimeValue = this.form()?.get('startTime')?.value;

          if (!startTimeValue && value) {
            this.form()?.get('startTime')?.setValue(startOfDay(value));
          }

          if (startTimeValue && value) {
            this.form()?.get('startTime')?.setValue(this.dateUtils.combineDateTime(value, startTimeValue));
            this.campaignDatesService.setStartDate(this.dateUtils.combineDateTime(value, startTimeValue));
          }

          this.checkDateConditions();
        }),
      );
    }

    const startTimeControl = this.form().get('startTime');

    if (startTimeControl) {
      this.subscriptions.push(
        startTimeControl.valueChanges.subscribe((value) => {
          this.startTimeSignal.set(value ? this.dateUtils.removeSecondsAndRest(value) : null);

          this.campaignDatesService.setStartDate(this.startTimeSignal()!);

          if (this.form()?.get('endTime')?.value) {
            this.form()?.get('endTime')?.markAsTouched();
          }

          this.checkDateConditions();
        }),
      );
    }
  }

  private subscribeToEndDateTimeChanges(): void {
    const endDateControl = this.form().get('endDate');

    if (endDateControl) {
      this.subscriptions.push(
        endDateControl.valueChanges.subscribe((value) => {
          const normalizedEndDate: Date | null = this.datePickerUtilities.getValidEndDate(
            value as Date | null,
            (this.form().get('startDate')?.value as Date | null) ?? null,
          );

          if (value && normalizedEndDate && value.getTime() !== normalizedEndDate.getTime()) {
            this.form()?.get('endDate')?.setValue(normalizedEndDate, { emitEvent: false });
          }

          this.endDateSignal.set(normalizedEndDate);

          const endTimeValue = this.form()?.get('endTime')?.value;

          if (!endTimeValue && normalizedEndDate) {
            this.form()?.get('endTime')?.setValue(endOfDay(normalizedEndDate));
          }

          if (endTimeValue && normalizedEndDate) {
            this.form()?.get('endTime')?.setValue(this.dateUtils.combineDateTime(normalizedEndDate, endTimeValue));
            this.endTimeSignal.set(this.dateUtils.combineDateTime(normalizedEndDate, endTimeValue));
          }

          this.campaignDatesService.setEndDate(this.endTimeSignal()!);

          this.checkDateConditions();
        }),
      );
    }

    const endTimeControl = this.form().get('endTime');

    if (endTimeControl) {
      this.subscriptions.push(
        endTimeControl.valueChanges.subscribe((value) => {
          this.endTimeSignal.set(value ? this.dateUtils.removeSecondsAndRest(value) : null);

          this.campaignDatesService.setEndDate(this.endTimeSignal()!);

          this.checkDateConditions();
        }),
      );
    }
  }

  private subscribeToPickerEvents(): void {
    if (this.startRangePicker()) {
      this.subscriptions.push(
        this.startRangePicker().openedStream.subscribe(() => this.syncPickerSelections()),
        this.startRangePicker().closedStream.subscribe(() => {
          this.ensureEndDateWhenOnlyStartSelected();
        }),
      );
    }

    if (this.endDatePicker()) {
      this.subscriptions.push(
        this.endDatePicker().openedStream.subscribe(() => this.syncPickerSelections()),
        this.endDatePicker().closedStream.subscribe(() => {
          this.ensureEndDateWhenOnlyStartSelected();
        }),
      );
    }
  }

  private unsubscribeAll(): void {
    this.subscriptions?.forEach((subscription) => subscription?.unsubscribe());
    this.subscriptions.length = 0;
  }

  private checkDateConditions(): void {
    const startDate = this.startDateSignal();
    const startTime = this.startTimeSignal();
    const endDate = this.endDateSignal();
    const endTime = this.endTimeSignal();

    if (!startDate || !startTime || !endDate || !endTime) {
      this.isViolatingMinimumDuration.set(false);
      this.form().controls.endDate.setErrors(null);

      return;
    }

    const startDateTime = this.dateUtils.combineDateTime(startDate, startTime);
    const endDateTime = this.dateUtils.combineDateTime(endDate, endTime);

    if (isSameDay(startDateTime, endDateTime) && isBefore(endDateTime, addMinutes(startDateTime, 59))) {
      this.isViolatingMinimumDuration.set(true);
      this.form().controls.endDate.setErrors({
        campaignDurationCannotBeLessThanOneHour: true,
      });
    } else {
      this.isViolatingMinimumDuration.set(false);
      this.form().controls.endDate.setErrors(null);
    }

    const campaignDatesError = this.campaignDurationUtilitiesService.getCampaignDatesError();

    if (campaignDatesError) {
      this.campaignDatesError.set(campaignDatesError);

      setTimeout(() => {
        this.form().setErrors({ error: true });
      });
    } else if (isNil(this.form().controls.startDate.errors)) {
      this.campaignDatesError.set(null);
      this.form().controls.startDate.setErrors(null);
    }
  }

  private syncPickerSelections(initialSync = false): void {
    if (this.isSyncingPicker) {
      return;
    }

    this.isSyncingPicker = true;

    const startDate: Date | null = (this.form().get('startDate')?.value as Date | null) || null;
    const endDate: Date | null = (this.form().get('endDate')?.value as Date | null) || null;

    setTimeout(() => {
      this.applySelectionToPicker(this.startRangePicker(), startDate, endDate);
      this.isSyncingPicker = false;

      this.form().controls.startDate.setValue(startDate);
      this.form().controls.endDate.setValue(endDate);

      if (initialSync) {
        this.form().controls.startDate.markAsPristine();
        this.form().controls.endDate.markAsPristine();
        this.form().controls.startDate.markAsUntouched();
        this.form().controls.endDate.markAsUntouched();
      }
    });
  }

  private applySelectionToPicker(
    picker: MatDateRangePicker<Date> | undefined,
    start: Date | null,
    end: Date | null,
  ): void {
    if (start) {
      picker?.select(start);
    }

    if (end) {
      picker?.select(end);
    }
  }

  private ensureEndDateWhenOnlyStartSelected(): void {
    const startDate = (this.form().get('startDate')?.value as Date | null) || null;
    const endDate = (this.form().get('endDate')?.value as Date | null) || null;

    if (startDate && !endDate) {
      this.form().get('endDate')?.setValue(startDate);
    }

    if (startDate && endDate && isBefore(startOfDay(endDate), startOfDay(startDate))) {
      this.form().get('endDate')?.setValue(startDate);
    }

    this.syncPickerSelections();
  }
}
